import { useState, type CSSProperties, type JSX } from 'react';

/**
 * Inline confirm for a destructive / hard-to-undo action (Disconnect Xero, Sign
 * out all devices). Sexton's law, standardised: reversible things act at once
 * (see <Toggle>); only the irreversible asks first — in place, no modal. The
 * button expands into a "prompt · Confirm · Cancel" row; Confirm awaits the
 * action and rolls the row back on error. Self-styled with tokens + fallbacks.
 */
export interface ConfirmActionProps {
  /** The resting button label, e.g. "Disconnect Xero". */
  label: string;
  onConfirm: () => void | Promise<void>;
  /** The one-line question shown while confirming. */
  prompt?: string;
  /** Confirm-button label (default "Confirm"). */
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  disabled?: boolean;
  onError?: (err: unknown) => void;
}

export function ConfirmAction({
  label,
  onConfirm,
  prompt,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  disabled,
  onError,
}: ConfirmActionProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const btn: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid var(--color-border, #d8d2c6)',
    background: 'var(--color-panel, #fff)',
    color: 'var(--color-ink, #111827)',
    borderRadius: 'var(--radius-sm, 8px)',
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  const accent: CSSProperties =
    tone === 'danger'
      ? {
          ...btn,
          color: '#fff',
          background: 'var(--color-danger, #b3261e)',
          border: '1px solid transparent',
        }
      : {
          ...btn,
          color: '#fff',
          background: 'var(--color-accent, #1f7a4d)',
          border: '1px solid transparent',
        };

  if (!confirming) {
    return (
      <button
        type="button"
        disabled={disabled === true}
        onClick={() => setConfirming(true)}
        style={{
          ...btn,
          color: tone === 'danger' ? 'var(--color-danger, #b3261e)' : 'var(--color-ink, #111827)',
          opacity: disabled === true ? 0.55 : 1,
        }}
      >
        {label}
      </button>
    );
  }

  const doConfirm = (): void => {
    setBusy(true);
    Promise.resolve(onConfirm())
      .then(() => setConfirming(false))
      .catch((err: unknown) => {
        setConfirming(false);
        onError?.(err);
      })
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {prompt && (
        <span style={{ fontSize: 13, color: 'var(--color-muted, #6b7280)' }}>{prompt}</span>
      )}
      <span style={{ display: 'inline-flex', gap: 8 }}>
        <button type="button" disabled={busy} onClick={doConfirm} style={accent}>
          {busy ? '…' : confirmLabel}
        </button>
        <button type="button" disabled={busy} onClick={() => setConfirming(false)} style={btn}>
          {cancelLabel}
        </button>
      </span>
    </div>
  );
}
